import { Module } from "../module";
import { SaveDataFileMessage, StartAvatarRecordingMessage, StopAvatarRecordingMessage, ControllerHand, RoomChangedMessage, ToastMessage } from "flavr-common";
import { CameraUpdateMessage } from "../../messages/rendering/MeshMessages";
import { ControllerConnectedMessage, ControllerDisconnectedMessage, ControllerMovedMessage } from "../../messages/controller/ControllerMessages";
const MIN_ADD_FRAME_TIME = 500;
class Track {
    constructor() {
        this.lastTimeStamp = 0;
        this.keyFrames = [];
    }
    clear() {
        this.keyFrames = [];
        this.lastTimeStamp = 0;
    }
    addFrame(started, timeStamp, pos, ori, scale) {
        const frame = {
            frame: (timeStamp - started) * 0.001,
            transform: {
                pos,
                ori,
                scale
            }
        };
        if (timeStamp - this.lastTimeStamp < MIN_ADD_FRAME_TIME) {
            this.lastUnsavedKeyFrame = frame;
            return;
        }
        this.lastTimeStamp = timeStamp;
        this.keyFrames.push(frame);
    }
    flush() {
        if (this.lastUnsavedKeyFrame === undefined)
            return;
        this.keyFrames.push(this.lastUnsavedKeyFrame);
    }
}
/**
 * Records the local player's head and hand-controller transforms into keyframe tracks while recording is active, and saves the captured take to a JSON file so it can be replayed as an avatar animation elsewhere.
 *
 * Messages:
 * - `StartAvatarRecordingMessage` and `StopAvatarRecordingMessage` - Start and stop capturing keyframes; stopping flushes the tracks and saves them via `SaveDataFileMessage`.
 * - `RoomChangedMessage` - Stops any active recording when the player changes rooms.
 * - `CameraUpdateMessage` and `ControllerMovedMessage` - While recording, append a keyframe (position/orientation/scale) to the avatar's or the respective controller's track.
 * - `ControllerConnectedMessage` and `ControllerDisconnectedMessage` - Create or remove the per-controller keyframe track as controllers connect and disconnect.
 */
export class AvatarRecorderModule extends Module {
    /**
     * Subscribes to camera and controller movement/connection updates so keyframes can be captured while recording,
     * and to the start/stop recording and room-change messages that control the recording life cycle.
     */
    constructor() {
        super("AvatarRecorder");
        this.started = 0;
        this.avatarTrack = new Track();
        this.controllerData = new Map();
        this.isRecording = false;
        this.handleCameraUpdateMessage = (msg, meta) => {
            if (!this.isRecording)
                return;
            this.avatarTrack.addFrame(this.started, meta.timeStamp, msg.position, msg.orientation, msg.platformScale);
        };
        this.handleControllerConnected = (msg) => {
            this.controllerData.set(msg.uid, {
                hand: `${this.controllerHandToString(msg.hand)}`,
                track: new Track()
            });
        };
        this.handleControllerDisconnected = (msg) => {
            this.controllerData.delete(msg.uid);
        };
        this.handleControllerMoved = (msg, meta) => {
            if (!this.isRecording)
                return;
            const data = this.controllerData.get(msg.uid);
            if (!data)
                return;
            data.track.addFrame(this.started, meta.timeStamp, msg.position, msg.orientation, msg.scale);
        };
        this.startRecording = () => {
            this.started = Date.now();
            this.isRecording = true;
            this.connection.send(new ToastMessage("Start recording"));
        };
        this.stopRecording = () => {
            if (!this.isRecording)
                return;
            this.isRecording = false;
            this.avatarTrack.flush();
            for (const data of this.controllerData.values()) {
                data.track.flush();
            }
            this.saveRecording();
            this.avatarTrack.clear();
            for (const data of this.controllerData.values()) {
                data.track.clear();
            }
            this.connection.send(new ToastMessage("Stop recording"));
        };
        this.connection.subscribeByType(CameraUpdateMessage, this.handleCameraUpdateMessage);
        this.connection.subscribeByType(ControllerConnectedMessage, this.handleControllerConnected);
        this.connection.subscribeByType(ControllerDisconnectedMessage, this.handleControllerDisconnected);
        this.connection.subscribeByType(ControllerMovedMessage, this.handleControllerMoved);
        this.connection.subscribeByType(StartAvatarRecordingMessage, this.startRecording);
        this.connection.subscribeByType(StopAvatarRecordingMessage, this.stopRecording);
        this.connection.subscribeByType(RoomChangedMessage, this.stopRecording);
    }
    /**
     * Keeps the standard module life-cycle contract even though this module finishes its setup in the constructor.
     */
    init() { }
    controllerHandToString(hand) {
        switch (hand) {
            case ControllerHand.LeftHand:
                return "left";
            case ControllerHand.RightHand:
                return "right";
            default:
                return "right";
        }
    }
    saveRecording() {
        const json = {};
        json.tracks = {
            avatar: {
                videoUrl: "",
                keyFrames: this.avatarTrack.keyFrames
            },
            controllers: []
        };
        let index = 0;
        for (const data of this.controllerData.values()) {
            json.tracks.controllers.push({
                index,
                hand: data.hand,
                keyFrames: data.track.keyFrames
            });
            index++;
        }
        const date = new Date();
        this.connection.send(new SaveDataFileMessage(JSON.stringify(json), `track_${date.toLocaleString()}.json`));
    }
}
